import { Component, ElementRef, EventEmitter, HostListener, Input, Output, ViewChild } from '@angular/core';

import { Menu } from 'app/shared/models/control/menu';
import { FlatMenuEntry } from 'app/shared/services/menu-search/menu-search.service';
import { NO_OPEN_SCREENS, OpenScreens } from 'app/shared/services/menu-open-screens/open-screens.service';

export type SearchScope = 'module' | 'all';

@Component({
    selector: 'app-shell-flyout',
    templateUrl: './app-shell-flyout.component.html',
    styleUrls: ['./app-shell-flyout.component.scss'],
})
export class AppShellFlyoutComponent {
    /**
     * How many rows Pinned shows before its "Show all" toggle. Four keeps the list plus the
     * module's own sections on screen together, which is the point of having it here rather than
     * on a separate page - a list long enough to push the tree out of view would just be a second
     * way to scroll.
     */
    readonly collapsedCount = 4;

    @Input() menuOpen = true;
    @Input() isHome = true;
    @Input() activeModule: Menu | undefined;
    @Input() openScreens: OpenScreens = NO_OPEN_SCREENS;
    @Input() query = '';
    @Input() searchResults: FlatMenuEntry[] = [];
    /** The user's favorites, resolved to rows - see AppShellComponent.pinnedRows. */
    @Input() pinnedRows: FlatMenuEntry[] = [];
    @Input() pinnedPaths: string[] = [];
    @Input() scope: SearchScope = 'module';
    /** False on Home, where the rail has no module selected to scope to. */
    @Input() canScopeToModule = false;
    @Input() moduleCount = 4;

    @Output() readonly queryChange = new EventEmitter<string>();
    @Output() readonly scopeChange = new EventEmitter<SearchScope>();
    @Output() readonly activate = new EventEmitter<Menu>();
    @Output() readonly togglePin = new EventEmitter<{ item: Menu; event: Event }>();
    @Output() readonly openMenu = new EventEmitter<void>();

    @ViewChild('searchInput') searchInput?: ElementRef<HTMLInputElement>;

    pinnedExpanded = false;

    @HostListener('window:keydown', ['$event'])
    onKeyDown(event: KeyboardEvent): void {
        const mod = event.ctrlKey || event.metaKey;
        if (mod && (event.key === 'k' || event.key === 'K')) {
            event.preventDefault();
            if (!this.menuOpen) {
                this.openMenu.emit();
            }
            this.focusSearchInput();
        } else if (event.key === 'Escape' && this.query) {
            this.setQuery('');
        }
    }

    private focusSearchInput(): void {
        // Deferred a frame so it runs after menuOpen flips true and the flyout's width
        // transition starts - focusing mid-collapse (width: 0) still works, but this keeps
        // focus+select from racing the @Input update when the sidebar was closed.
        requestAnimationFrame(() => {
            this.searchInput?.nativeElement.focus();
            this.searchInput?.nativeElement.select();
        });
    }

    get sectionsForActiveModule(): Menu[] {
        return this.activeModule?.children || [];
    }

    /**
     * Root of the key chain the tree rows identify themselves by - it has to start at the module
     * so the keys line up with the ones OpenScreensService builds walking PagesMenu from its top.
     */
    get activeModuleKey(): string {
        return this.activeModule ? `${this.activeModule.id}` : '';
    }

    setQuery(value: string): void {
        this.queryChange.emit(value);
    }

    setScope(scope: SearchScope): void {
        this.scopeChange.emit(scope);
    }

    onRowClick(entry: FlatMenuEntry): void {
        this.activate.emit(entry.item);
    }

    onRowPin(entry: FlatMenuEntry, event: Event): void {
        event.stopPropagation();
        this.togglePin.emit({ item: entry.item, event });
    }

    isPinned(entry: FlatMenuEntry): boolean {
        return this.pinnedPaths.includes(entry.item.path);
    }

    /** Open in a window somewhere - possibly minimized, possibly behind others. */
    isOpen(entry: FlatMenuEntry): boolean {
        return this.openScreens.openKeys.has(entry.key);
    }

    /** The screen in the front-most window, i.e. the one being looked at right now. */
    isActive(entry: FlatMenuEntry): boolean {
        return this.openScreens.activeKey === entry.key;
    }

    /**
     * Whether the row at `index` is past the collapsed cut-off. Written as a per-row test rather
     * than a `rows.slice()` getter because these lists are read on every change-detection pass; a
     * getter returning a fresh array each time would make *ngFor re-diff a list that has not moved.
     */
    isBeyondCollapsed(index: number, expanded: boolean): boolean {
        return !expanded && index >= this.collapsedCount;
    }

    /**
     * PagesMenu ids collide (see MenuKey), and two colliding screens can land in one list - a
     * search for a word both share, or both being pinned - so rows are tracked by their key chain.
     */
    trackByRowKey(_index: number, row: FlatMenuEntry): string {
        return row.key;
    }
}
